import clsx from "clsx";
import PropTypes from "prop-types";
import { Icon } from "@iconify/react";
import React, { useState } from "react";
import { NavLink as RouterLink } from "react-router-dom";
import arrowIosForwardFill from "@iconify-icons/eva/arrow-ios-forward-fill";
import arrowIosDownwardFill from "@iconify-icons/eva/arrow-ios-downward-fill";
import { alpha, makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Collapse,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@material-ui/core";
import useAuth from "src/hooks/useAuth";
import Roles from "src/utils/Roles";
import IsRole from "src/utils/IsRole";
import { useSelector, useDispatch } from "src/redux/store";
import { PATH_APP } from "src/routes/paths";
import { openContractDialog } from "src/redux/slices/addOns";

// ----------------------------------------------------------------------

const useStyles = makeStyles((theme) => ({
  root: {},
  listItem: {
    ...theme.typography.body2,
    height: 48,
    textTransform: "capitalize",
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(2.5),
    color: theme.palette.text.secondary,
  },
  subIcon: {
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:before": {
      width: 4,
      height: 4,
      content: "''",
      display: "block",
      borderRadius: "50%",
      backgroundColor: theme.palette.text.disabled,
      transition: theme.transitions.create("transform"),
    },
  },
  isActiveListItem: {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
    backgroundColor: alpha(
      theme.palette.primary.main,
      theme.palette.action.selectedOpacity
    ),
    "&:before": {
      top: 0,
      right: 0,
      width: 3,
      bottom: 0,
      content: "''",
      position: "absolute",
      backgroundColor: theme.palette.primary.main,
    },
  },
  isActiveListItemSub: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
    "& $subIcon:before": {
      transform: "scale(2)",
      backgroundColor: theme.palette.primary.main,
    },
  },
}));

// ----------------------------------------------------------------------

NavItem.propTypes = {
  level: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  href: PropTypes.string,
  info: PropTypes.element,
  icon: PropTypes.element,
  open: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
};

function NavItem({
  level,
  title,
  href,
  info,
  icon,
  open = false,
  children,
  className,
  ...other
}) {
  const classes = useStyles();
  const [show, setShow] = useState(open);
  const dispatch = useDispatch();
  const { claims, user } = useSelector((state) => state.authJwt);
  const isSubItem = level > 0;

  const handleShow = () => {
    setShow((show) => !show);
  };

  // F64 · href tipo "#addon:Reputación online" abre modal de contratación
  // Stripe directo en vez de navegar. Intercepta click y dispatch al store.
  const isAddOnCta = typeof href === "string" && href.startsWith("#addon:");
  const handleAddOnCtaClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const name = href.substring("#addon:".length);
    dispatch(openContractDialog(name));
  };

  if (children) {
    return (
      <>
        <ListItem
          button
          disableGutters
          disabled={
            href !== PATH_APP.user.root &&
            new Date(user?.finishDemoTrial) < new Date() &&
            !IsRole(claims.role, Roles.GLOBAL_ADMIN) &&
            !IsRole(claims.role, Roles.USER_DISTRIBUTOR)
          }
          onClick={handleShow}
          className={clsx(
            classes.listItem,
            {
              [classes.isActiveListItem]: open,
            },
            className
          )}
          {...other}
        >
          <ListItemIcon>{icon && icon}</ListItemIcon>
          <ListItemText disableTypography primary={title} />
          {info && info}
          <Box
            component={Icon}
            icon={show ? arrowIosDownwardFill : arrowIosForwardFill}
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItem>

        <Collapse in={show}>{children}</Collapse>
      </>
    );
  }

  // F64 · CTA de add-on premium (href="#addon:XXX") — sin RouterLink,
  // click intercepta para abrir modal Stripe. Título limpio + badge
  // "PRO" dorado ámbar a la derecha (estilo Notion/Figma/Stripe) —
  // señaliza que la feature es de pago sin ensuciar el nombre.
  if (isAddOnCta) {
    return (
      <ListItem
        button
        disableGutters
        onClick={handleAddOnCtaClick}
        className={clsx(classes.listItem, className)}
        sx={{
          "&:hover": { bgcolor: "rgba(245, 158, 11, 0.08)" },
        }}
        {...other}
      >
        <ListItemIcon className={classes.listItemIcon}>
          {isSubItem ? <span className={classes.subIcon} /> : icon}
        </ListItemIcon>
        <ListItemText disableTypography primary={title} />
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            fontSize: 10,
            fontWeight: 900,
            letterSpacing: 0.6,
            color: "#78350f",
            bgcolor: "#fbbf24",
            borderRadius: "999px",
            px: 0.75,
            py: 0.15,
            mr: 1,
            boxShadow: "0 1px 2px rgba(180, 83, 9, 0.25)",
          }}
        >
          PRO
        </Box>
        {info && info}
      </ListItem>
    );
  }

  return (
    <ListItem
      button
      to={href}
      exact={open}
      disableGutters
      disabled={
        href !== PATH_APP.user.root &&
        new Date(user?.finishDemoTrial) < new Date() &&
        !IsRole(claims.role, Roles.GLOBAL_ADMIN) &&
        !IsRole(claims.role, Roles.USER_DISTRIBUTOR)
      }
      component={RouterLink}
      activeClassName={
        isSubItem ? classes.isActiveListItemSub : classes.isActiveListItem
      }
      className={clsx(classes.listItem, className)}
      isActive={(match, location) => {
        if (!match) {
          return false;
        }
        const { url } = match;
        const { pathname } = location;
        const isMatch = url === pathname;

        if (!isSubItem) {
          return url.length && pathname.includes(url);
        }

        return isMatch;
      }}
      {...other}
    >
      <ListItemIcon className={classes.listItemIcon}>
        {isSubItem ? <span className={classes.subIcon} /> : icon}
      </ListItemIcon>
      <ListItemText disableTypography primary={title} />

      {info && info}
    </ListItem>
  );
}

export default NavItem;
